import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BundleFlightData } from '../FlightDetails/flightSlice';
import { OptionalFilter } from '../../components/SideFilters/interface';

export interface SupplierError {
  ts_id: number;
  ts_type?: string;
  error: string;
}

export type AsyncSearchStatus =
  | 'idle'
  | 'loading_all'
  | 'first_chunk_received'
  | 'streaming'
  | 'complete'
  | 'error';

export interface FlightAsyncState {
  // State machine
  status: AsyncSearchStatus;

  // Results accumulation
  flights: BundleFlightData[];

  // Supplier tracking
  expectedSuppliers: number;
  returnedSuppliers: number;
  supplierErrors: SupplierError[];

  // Client-built filters
  optionalFilters: OptionalFilter[];

  // Error
  error: string | null;

  // Search metadata
  searchStartTime: number | null;
  searchEndTime: number | null;

  // Progress tracking from SSE
  totalCount: number;
  successCount: number;
  errorCount: number;
}

const initialState: FlightAsyncState = {
  status: 'idle',
  flights: [],
  expectedSuppliers: 0,
  returnedSuppliers: 0,
  supplierErrors: [],
  optionalFilters: [], // Empty array instead of null so filters render immediately with skeletons
  error: null,
  searchStartTime: null,
  searchEndTime: null,
  totalCount: 0,
  successCount: 0,
  errorCount: 0,
};

export const flightAsyncSlice = createSlice({
  name: 'flightAsync',
  initialState,
  reducers: {
    startSSESearch: (state) => {
      state.status = 'loading_all';
      state.flights = [];
      state.returnedSuppliers = 0;
      state.supplierErrors = [];
      state.error = null;
      state.optionalFilters = []; // Empty array so filters can render with skeletons while loading
      state.searchStartTime = Date.now();
      state.searchEndTime = null;
      state.totalCount = 0;
      state.successCount = 0;
      state.errorCount = 0;
    },

    setExpectedSuppliers: (state, action: PayloadAction<number>) => {
      state.expectedSuppliers = action.payload;
    },

    receiveFirstChunk: (
      state,
      action: PayloadAction<{
        flights: BundleFlightData[];
        ts_id: number;
        ts_type?: string;
      }>
    ) => {
      state.status = 'first_chunk_received';
      state.flights.push(...action.payload.flights);
      state.returnedSuppliers += 1;
    },

    receiveChunk: (
      state,
      action: PayloadAction<{
        flights: BundleFlightData[];
        ts_id: number;
        ts_type?: string;
      }>
    ) => {
      if (
        state.status === 'first_chunk_received' ||
        state.status === 'loading_all'
      ) {
        state.status = 'streaming';
      }
      state.flights.push(...action.payload.flights);
      state.returnedSuppliers += 1;
    },

    receiveSupplierError: (state, action: PayloadAction<SupplierError>) => {
      state.supplierErrors.push(action.payload);
      state.returnedSuppliers += 1;
    },

    updateOptionalFilters: (state, action: PayloadAction<OptionalFilter[]>) => {
      state.optionalFilters = action.payload;
    },

    updateProgress: (
      state,
      action: PayloadAction<{
        totalCount: number;
        successCount: number;
        errorCount: number;
      }>
    ) => {
      state.totalCount = action.payload.totalCount;
      state.successCount = action.payload.successCount;
      state.errorCount = action.payload.errorCount;
    },

    completeSSESearch: (state) => {
      state.status = 'complete';
      state.searchEndTime = Date.now();
    },

    errorSSESearch: (state, action: PayloadAction<string>) => {
      state.status = 'error';
      state.error = action.payload;
      state.searchEndTime = Date.now();
    },

    resetAsyncSearch: (state) => {
      return initialState;
    },
  },
});

export const {
  startSSESearch,
  setExpectedSuppliers,
  receiveFirstChunk,
  receiveChunk,
  receiveSupplierError,
  updateOptionalFilters,
  updateProgress,
  completeSSESearch,
  errorSSESearch,
  resetAsyncSearch,
} = flightAsyncSlice.actions;

// Selectors
export const selectAsyncSearchStatus = (state: {
  flightAsync: FlightAsyncState;
}) => state.flightAsync.status;

export const selectAsyncFlights = (state: { flightAsync: FlightAsyncState }) =>
  state.flightAsync.flights;

export const selectAsyncOptionalFilters = (state: {
  flightAsync: FlightAsyncState;
}) => state.flightAsync.optionalFilters;

export const selectAsyncSupplierProgress = (state: {
  flightAsync: FlightAsyncState;
}) => ({
  expected: state.flightAsync.expectedSuppliers,
  returned: state.flightAsync.returnedSuppliers,
  errors: state.flightAsync.supplierErrors,
});

export const selectAsyncError = (state: { flightAsync: FlightAsyncState }) =>
  state.flightAsync.error;

export const selectAsyncSearchDuration = (state: {
  flightAsync: FlightAsyncState;
}) => {
  if (state.flightAsync.searchStartTime && state.flightAsync.searchEndTime) {
    return state.flightAsync.searchEndTime - state.flightAsync.searchStartTime;
  }
  return null;
};

export const selectAsyncProgress = (state: {
  flightAsync: FlightAsyncState;
}) => {
  const { totalCount, successCount, errorCount } = state.flightAsync;
  const completed = successCount + errorCount;
  const percentage = totalCount > 0 ? (completed / totalCount) * 100 : 0;
  return { totalCount, successCount, errorCount, completed, percentage };
};

export default flightAsyncSlice.reducer;
